// src/components/Navbar/Navbar.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./Navbar.module.css";

import logoName from "../../assets/logoName.png";
import AvatarPlaceholder from "../../assets/placeholder.png";
import { UserContext } from "../../context/UserContext";

export default function Navbar() {
  const { userData, user, userLogout } = useContext(UserContext);
  const { t, i18n } = useTranslation(["common", "navbar"]);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [langModalOpen, setLangModalOpen] = useState(false);
  const drawerRef = useRef(null);
  const firstLinkRef = useRef(null);
  const langFirstRef = useRef(null);
  const langTriggerRef = useRef(null);

  const userLocalData = useMemo(() => {
    try {
      const raw = localStorage.getItem("ibhava_last_quiz_payload");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed;
    } catch (e) {
      return null;
    }
  }, []);

  const onChangeLang = (lng) => {
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem("i18nextLng", lng);
    } catch (e) {}
    setLangModalOpen(false);
    // return focus to trigger
    setTimeout(() => langTriggerRef.current?.focus(), 40);
  };

  // lock body scroll when drawer open or lang modal open
  useEffect(() => {
    if (drawerOpen || langModalOpen) {
      document.body.style.overflow = "hidden";
      if (drawerOpen) setTimeout(() => firstLinkRef.current?.focus(), 120);
      if (langModalOpen) setTimeout(() => langFirstRef.current?.focus(), 80);
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen, langModalOpen]);

  // close on ESC (both drawer and lang modal)
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        if (langModalOpen) setLangModalOpen(false);
        else setDrawerOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [langModalOpen]);

  // when navigating (route changes) ensure drawer closed
  useEffect(() => {
    return navigate.listen?.(() => setDrawerOpen(false));
  }, [navigate]);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.container}>
          <div className={styles.brand}>
            <Link to="/" aria-label="Ibhava home">
              <img src={logoName} alt="Ibhava" className={styles.logo} />
            </Link>
          </div>

          <div className={styles.actions}>
            {/* LANGUAGE TRIGGER ICON */}
            <div className={styles.lang}>
              <button
                ref={langTriggerRef}
                className={styles.langIcon}
                aria-haspopup="dialog"
                aria-expanded={langModalOpen}
                aria-controls="lang-modal"
                onClick={() => setLangModalOpen((s) => !s)}
                title={i18n.language === "pt" ? "Português" : "English"}
              >
                {/* globe icon */}
                <i className="material-symbols-outlined">language</i>
                <span className={styles.langAbbr}>
                  {i18n.language?.toUpperCase?.() || "PT"}
                </span>
              </button>
            </div>

            {/*
              Keep CTA / auth / profile as before
            */}

            {user ? (
              <div className={styles.profileMenu}>
                {/* <img
                  src={user.avatarUrl || AvatarPlaceholder}
                  alt={t("navbar:avatar_alt", { name: user.name || "" })}
                  className={styles.avatar}
                  onClick={() => navigate("/profile")}
                /> */}
                <button
                  className={styles.logout}
                  onClick={() => {
                    userLogout();
                    navigate("/");
                  }}
                >
                  {t("navbar:logout")}
                </button>
              </div>
            ) : (
              <div className={styles.auth}>
                <button
                  onClick={() => navigate("/login")}
                  className={styles.signup}
                >
                  {t("navbar:login")}
                </button>
              </div>
            )}

            {/* Hamburger (mobile) */}
            <button
              className={styles.hamburger}
              aria-label={drawerOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={drawerOpen}
              aria-controls="mobile-drawer"
              onClick={() => setDrawerOpen((s) => !s)}
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d={
                    drawerOpen
                      ? "M6 6 L18 18 M6 18 L18 6"
                      : "M3 6h18M3 12h18M3 18h18"
                  }
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* LANGUAGE MODAL */}
      {langModalOpen && (
        <div
          id="lang-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lang-title"
          className={styles.langModalOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setLangModalOpen(false);
          }}
        >
          <div className={styles.langModal} role="document">
            <h3 id="lang-title" className={styles.langModalTitle}>
              Idioma / Language
            </h3>

            <div className={styles.langOptions}>
              <button
                ref={langFirstRef}
                className={`${styles.langOption} ${
                  i18n.language === "pt" ? styles.langActive : ""
                }`}
                onClick={() => onChangeLang("pt")}
                aria-pressed={i18n.language === "pt"}
              >
                Português (PT)
              </button>

              <button
                className={`${styles.langOption} ${
                  i18n.language === "en" ? styles.langActive : ""
                }`}
                onClick={() => onChangeLang("en")}
                aria-pressed={i18n.language === "en"}
              >
                English (EN)
              </button>
            </div>

            <button
              className={styles.langClose}
              onClick={() => setLangModalOpen(false)}
              aria-label="Fechar seleção de idioma"
            >
              Fechar
            </button>
          </div>
        </div>
      )}

      {/* Mobile Drawer (mantive igual ao seu original) */}
      <div
        id="mobile-drawer"
        role="dialog"
        aria-modal="true"
        className={`${styles.drawerWrap} ${drawerOpen ? styles.open : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setDrawerOpen(false);
        }}
      >
        <aside className={styles.drawer} ref={drawerRef}>
          <div className={styles.drawerHeader}>
            <Link
              to="/"
              onClick={() => setDrawerOpen(false)}
              className={styles.drawerBrand}
            >
              <img src={logoName} alt="Ibhava" className={styles.drawerLogo} />
            </Link>
            <button
              className={styles.closeBtn}
              onClick={() => setDrawerOpen(false)}
              aria-label="Fechar menu"
            >
              ✕
            </button>
          </div>

          <nav className={styles.drawerNav} aria-label="Menu móvel">
            {user ? (
              <div className={styles.profileMenuMobile}>
                <img
                  src={user.avatarUrl || AvatarPlaceholder}
                  alt={t("navbar:avatar_alt", { name: user.name || "" })}
                  className={styles.avatar}
                  onClick={() => {
                    setDrawerOpen(false);
                    navigate("/profile");
                  }}
                />
                <button
                  className={styles.logoutMobile}
                  onClick={() => {
                    userLogout();
                    setDrawerOpen(false);
                    navigate("/");
                  }}
                >
                  {t("navbar:logout")}
                </button>
              </div>
            ) : (
              <div className={styles.authMobile}>
                <button
                  onClick={() => {
                    navigate("/login");
                    setDrawerOpen(false);
                  }}
                  className={styles.signup}
                >
                  {t("navbar:login")}
                </button>
              </div>
            )}
            <div className={styles.langMobile}>
              <button
                className={`${styles.langBtn} ${
                  i18n.language === "pt" ? styles.active : ""
                }`}
                onClick={() => onChangeLang("pt")}
                aria-label="Mudar idioma para Português"
              >
                PT
              </button>
              <button
                className={`${styles.langBtn} ${
                  i18n.language === "en" ? styles.active : ""
                }`}
                onClick={() => onChangeLang("en")}
                aria-label="Mudar idioma para English"
              >
                EN
              </button>
            </div>
          </nav>
        </aside>
      </div>
    </>
  );
}
